import React, { useEffect, useState } from 'react';
import { BaseAutocomplete } from '@askporter/component-library/BaseAutocomplete';
import { BaseAutocompleteValue } from '@askporter/component-library/RevampedAutocomplete/types';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system/styleFunctionSx';
import { GenericSearchResult, UseSearchAutocompleteProps } from '../../hooks/useSearchAutocomplete';
export type RevampedSearchAutocompleteProps<SearchResult> = UseSearchAutocompleteProps<SearchResult[]> & {
  disabled?: boolean;
  onChange: (searchResult: SearchResult | SearchResult[] | null) => void;
  value?: SearchResult | SearchResult[];
  t: (key: string, options?: Record<string, string | number>) => string;
  idPrefix?: string;
  textFieldSx?: SxProps<Theme>;
  error?: boolean;
  label?: string;
  placeholder?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  getOptionLabel?: (option: SearchResult) => string;
  isOptionEqualToValue?: (option: SearchResult, value: SearchResult) => boolean;
  multiple?: boolean;
  sx?: SxProps<Theme>;
  onInputChange?: (event: React.SyntheticEvent, newInputValue: string) => void;
  readOnly?: boolean;
  readOnlyText?: React.ReactNode;
  clearable?: boolean;
};
/**
 * RevampedSearchAutocomplete component that provides infinite scrolling autocomplete with search functionality.
 * Built on top of BaseAutocomplete for modern architecture while preserving search-specific behavior.
 */
function RevampedSearchAutocomplete<SearchResult extends GenericSearchResult>({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  request,
  searchValue,
  setRequest,
  setSearchValue,
  status,
  t,
  idPrefix = 'search',
  error,
  label,
  placeholder,
  startAdornment,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  endAdornment,
  getOptionLabel,
  isOptionEqualToValue,
  multiple = false,
  options,
  value,
  disabled,
  textFieldSx,
  onInputChange: externalOnInputChange,
  readOnly = false,
  readOnlyText,
  clearable = false,
}: RevampedSearchAutocompleteProps<SearchResult>): React.ReactElement {
  // Track local clearing state to prevent value from controlling input when clearing
  const [isClearing, setIsClearing] = useState(false);
  // Convert SearchResult to BaseAutocompleteValue
  const mapSearchResultToBaseValue = (
    option: SearchResult,
  ): Omit<BaseAutocompleteValue, 'label'> & {
    label: string;
  } => ({
    uid: option.uid,
    label: getOptionLabel
      ? getOptionLabel(option)
      : (
          option as {
            title?: string;
            name?: string;
          }
        ).title ||
        (
          option as {
            title?: string;
            name?: string;
          }
        ).name ||
        option.uid,
    avatar: (
      option as {
        avatar?: string;
      }
    ).avatar,
    iconPath: (
      option as {
        iconPath?: string;
      }
    ).iconPath,
  });
  // Convert BaseAutocompleteValue back to SearchResult
  const mapBaseValueToSearchResult = (baseValue: BaseAutocompleteValue): SearchResult | null => {
    return options.find((option) => option.uid === baseValue.uid) || null;
  };
  // Transform options to BaseAutocompleteValue format
  const baseOptions: BaseAutocompleteValue[] = options.map(mapSearchResultToBaseValue);
  // Transform current value, but use null/[] when clearing to prevent MUI from controlling the input
  const baseValue = isClearing
    ? multiple
      ? []
      : null
    : value
    ? Array.isArray(value)
      ? value.map(mapSearchResultToBaseValue)
      : mapSearchResultToBaseValue(value)
    : multiple
    ? []
    : null;
  // Clear searchValue when value is cleared externally by parent, and reset clearing state
  useEffect(() => {
    if (multiple) {
      if (Array.isArray(value) && value.length === 0) {
        setSearchValue('');
        setIsClearing(false); // Reset clearing state when parent value is empty array
      }
    } else {
      if (!value) {
        setSearchValue('');
        setIsClearing(false); // Reset clearing state when parent value is null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, multiple]);
  // Handle infinite scroll
  const handleScrollToBottom = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };
  // Handle change events
  const handleChange = (newValue: BaseAutocompleteValue | BaseAutocompleteValue[] | null) => {
    setRequest(false);
    // Check if we're clearing the value
    const isClearingValue = multiple ? Array.isArray(newValue) && newValue.length === 0 : !newValue;
    // Clear searchValue immediately when clearing to prevent flicker
    if (isClearingValue) {
      setSearchValue('');
      setIsClearing(true);
    }
    if (multiple) {
      const searchResults = Array.isArray(newValue)
        ? (newValue.map(mapBaseValueToSearchResult).filter(Boolean) as SearchResult[])
        : [];
      onChange(searchResults);
    } else {
      const searchResult = newValue && !Array.isArray(newValue) ? mapBaseValueToSearchResult(newValue) : null;
      onChange(searchResult as SearchResult);
    }
  };
  // Handle input change
  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    // Call external onInputChange handler if provided
    if (externalOnInputChange) {
      externalOnInputChange(event, newInputValue);
    }
    // Always update the search value to allow typing
    setSearchValue(newInputValue);
    // Clear selection when user starts typing something different
    // This prevents the input from being controlled by the selected value's label
    if (value && newInputValue.length > 0) {
      const currentValueLabel = multiple
        ? (value as SearchResult[])
            .map((v) =>
              getOptionLabel
                ? getOptionLabel(v)
                : (
                    v as {
                      title?: string;
                      name?: string;
                    }
                  ).title ||
                  (
                    v as {
                      title?: string;
                      name?: string;
                    }
                  ).name ||
                  v.uid,
            )
            .join(', ')
        : getOptionLabel
        ? getOptionLabel(value as SearchResult)
        : (
            value as {
              title?: string;
              name?: string;
            }
          ).title ||
          (
            value as {
              title?: string;
              name?: string;
            }
          ).name ||
          (
            value as {
              uid: string;
            }
          ).uid;
      // If the new input is different from the selected value's label, clear the selection
      if (newInputValue !== currentValueLabel) {
        if (multiple) {
          onChange([]);
        } else {
          onChange(null);
        }
      }
    }
    // Trigger search when user types (similar to onKeyPress in old implementation)
    if (newInputValue.length > 2) {
      setRequest(true);
    }
  };
  // Handle blur
  const handleBlur = () => {
    setRequest(false);
  };
  // Loading state
  const isLoading = status === 'pending' || isFetchingNextPage;
  const commonProps = {
    t,
    idPrefix,
    freeSolo: true,
    options: baseOptions,
    onChange: handleChange,
    mapOptionToValue: (option: BaseAutocompleteValue) => ({
      ...option,
      label: option.label || option.uid, // Ensure label is always a string
    }),
    onScrollToBottom: handleScrollToBottom,
    onInputChange: handleInputChange,
    onBlur: handleBlur,
    inputValue: searchValue,
    disabled,
    error,
    label,
    placeholder: placeholder || t('ns.common:type_to_search'),
    startAdornment: startAdornment as React.ReactElement,
    loading: isLoading,
    readOnly,
    readOnlyText,
    isListAvatarVisible: false,
    showLabel: !!label,
    forcePopupIcon: !value,
    clearable,
    ...(isOptionEqualToValue && {
      isOptionEqualToValue: (option: BaseAutocompleteValue, val: BaseAutocompleteValue) => {
        const originalOption = mapBaseValueToSearchResult(option);
        const originalValue = mapBaseValueToSearchResult(val);
        return originalOption && originalValue ? isOptionEqualToValue(originalOption, originalValue) : false;
      },
    }),
  };
  const wrapperSx: SxProps<Theme> | undefined = textFieldSx
    ? ({
        '& .MuiInputBase-root': textFieldSx,
      } as SystemStyleObject<Theme> & Record<string, unknown>)
    : undefined;
  return multiple ? (
    <Box sx={wrapperSx}>
      <BaseAutocomplete {...commonProps} multiple={true} value={baseValue as BaseAutocompleteValue[]} />
    </Box>
  ) : (
    <Box sx={wrapperSx}>
      <BaseAutocomplete {...commonProps} multiple={false} value={baseValue as BaseAutocompleteValue | null} />
    </Box>
  );
}
export default RevampedSearchAutocomplete;
